// pages/EditProduct.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";
import ProductNavbar from "./ProductNavbar";
import Select from "react-select"; // ✅ Brand dropdown

function EditProduct() {
  const { uuid } = useParams();
  const [product, setProduct] = useState({
    name: "",
    category: "",
    subcategory: "",
    brand: "",
    price: "",
    mrp: "",
    stock: "",
    sku: "",
    description: "",
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch categories
  useEffect(() => {
    axios
      .get(`${baseurl}getCategories`)
      .then((res) => setCategories(res.data.categories))
      .catch((err) => console.error("Failed to fetch categories:", err));
  }, []);

  // ✅ Fetch product by UUID
  useEffect(() => {
    if (uuid) {
      axios
        .get(`${baseurl}getVendorProductByUuid/${uuid}`)
        .then(async (res) => {
          const p = res.data.product;

          setProduct({
            name: p.name || "",
            category: p.category?.id || "",
            subcategory: p.subcategory?.id || "",
            brand: p.brand_id || "",
            price: p.price || "",
            mrp: p.mrp || "",
            stock: p.stock || "",
            sku: p.sku || "",
            description: p.description || "",
          });

          // Fetch related subcategories and brands
          if (p.category?.id) {
            try {
              const [subRes, brandRes] = await Promise.all([
                axios.get(`${baseurl}categories/${p.category.id}/subcategories`),
                axios.get(`${baseurl}getAllBrands`),
              ]);

              // ✅ Filter brands for this category
              const filteredBrands = (brandRes.data.brands || []).filter(
                (b) => Number(b.category_id) === Number(p.category.id)
              );

              // ✅ Add “Other” manually
              const extendedBrands = [
                ...filteredBrands,
                { brand_id: "other", brand_name: "Other" },
              ];

              setSubcategories(subRes.data.subcategories || []);
              setBrands(extendedBrands);
            } catch (err) {
              console.error("Failed to fetch subcategories/brands:", err);
              setSubcategories([]);
              setBrands([]);
            }
          } else {
            setSubcategories([]);
            setBrands([]);
          }

          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to fetch product:", err);
          Swal.fire({ icon: "error", title: "Failed to load product!" });
          setLoading(false);
        });
    }
  }, [uuid]);

  // ✅ Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct({ ...product, [name]: value });
  };

  // ✅ Handle brand select
  const handleBrandSelect = (selectedOption) => {
    setProduct({ ...product, brand: selectedOption ? selectedOption.value : "" });
  };

  // ✅ Category change → refresh subcategories & brands
  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    setProduct({ ...product, category: categoryId, subcategory: "", brand: "" });

    if (!categoryId) {
      setSubcategories([]);
      setBrands([]);
      return;
    }

    try {
      const [subRes, brandRes] = await Promise.all([
        axios.get(`${baseurl}categories/${categoryId}/subcategories`),
        axios.get(`${baseurl}getAllBrands`),
      ]);

      const filteredBrands = (brandRes.data.brands || []).filter(
        (b) => Number(b.category_id) === Number(categoryId)
      );

      const extendedBrands = [
        ...filteredBrands,
        { brand_id: "other", brand_name: "Other" },
      ];

      setSubcategories(subRes.data.subcategories || []);
      setBrands(extendedBrands);
    } catch (err) {
      console.error("Failed to fetch subcategories or brands:", err);
      setSubcategories([]);
      setBrands([]);
    }
  };

  // ✅ Subcategory change
  const handleSubcategoryChange = (e) => {
    setProduct({ ...product, subcategory: e.target.value });
  };

  const vendorId = localStorage.getItem("vendorId");

const [submitting, setSubmitting] = useState(false);

  // ✅ Submit update (flag: redirectNext = true/false)
const handleSubmit = async (e, redirectNext = false) => {
  e.preventDefault();
  setSubmitting(true); // start loader

  try {
    const payload = { ...product, vendor_id: vendorId, uuid };

    await axios.put(`${baseurl}updateVendorProduct`, payload);

    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: "Product updated successfully!",
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      background: "#f0f9ff",
      color: "#0b3d91",
      iconColor: "#0b3d91",
    });

    // Redirect only if "Update & Next" is clicked
    if (redirectNext) {
      setTimeout(() => {
        window.location.href = `/product/gallery/${uuid}`;
      }, 2100);
    }
  } catch (error) {
    console.error("Error updating product:", error);
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "error",
      title: "Failed to update product!",
      showConfirmButton: false,
      timer: 2500,
      background: "#fff0f0",
      color: "#c00",
      iconColor: "#c00",
    });
  } finally {
    setSubmitting(false); // stop loader
  }
};

  // ✅ Spinner
  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "4rem", height: "4rem" }}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 fw-bold">Loading product data...</p>
      </div>
    );

  // ✅ Prepare React Select options
  const brandOptions = brands.map((b) => ({
    value: b.brand_id,
    label: b.brand_name,
  }));

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">
          <ProductNavbar />

          <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
            <div className="breadcrumb-title pe-3">Edit Product</div>
          </div>

          <div className="col-12 mb-4">
            <div className="card">
              <div className="card-header">Basic Product Info</div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    {/* Product Name */}
                    <div className="col-md-6 mb-3">
                      <label>Product Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={product.name}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Category */}
                    <div className="col-md-6 mb-3">
                      <label>Category</label>
                      <select
                        className="form-control"
                        value={product.category}
                        onChange={handleCategoryChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Subcategory */}
                    <div className="col-md-6 mb-3">
                      <label>Subcategory</label>
                      <select
                        className="form-control"
                        value={product.subcategory}
                        onChange={handleSubcategoryChange}
                        required
                        disabled={!product.category}
                      >
                        <option value="">Select Subcategory</option>
                        {subcategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* ✅ Brand (React Select) */}
                    <div className="col-md-6 mb-3">
                      <label>Brand (Optional)</label>
                      <Select
                        options={brandOptions}
                        value={
                          brandOptions.find(
                            (opt) => opt.value === product.brand
                          ) || null
                        }
                        onChange={handleBrandSelect}
                        placeholder="Search or select brand..."
                        isClearable
                      />
                    </div>

                    {/* Price / MRP / Stock / SKU */}
                    <div className="col-md-3 mb-3">
                      <label>Price</label>
                      <input
                        type="number"
                        className="form-control"
                        name="price"
                        value={product.price}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label>MRP</label>
                      <input
                        type="number"
                        className="form-control"
                        name="mrp"
                        value={product.mrp}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label>Stock</label>
                      <input
                        type="number"
                        className="form-control"
                        name="stock"
                        value={product.stock}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label>SKU</label>
                      <input
                        type="text"
                        className="form-control"
                        name="sku"
                        value={product.sku}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Description */}
                    <div className="col-12 mb-3">
                      <label>Description</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={product.description}
                        onChange={(event, editor) =>
                          setProduct({
                            ...product,
                            description: editor.getData(),
                          })
                        }
                        onReady={(editor) => {
                          editor.editing.view.change((writer) => {
                            writer.setStyle(
                              "min-height",
                              "300px",
                              editor.editing.view.document.getRoot()
                            );
                          });
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-12 mt-2 text-end">
  <button
    type="button"
    className="btn btn-secondary me-2"
    onClick={(e) => handleSubmit(e, false)}
    disabled={submitting}
  >
    {submitting ? (
      <>
        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
        Updating...
      </>
    ) : (
      "Update"
    )}
  </button>

  <button
    type="button"
    className="btn btn-primary"
    onClick={(e) => handleSubmit(e, true)}
    disabled={submitting}
  >
    {submitting ? (
      <>
        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
        Updating...
      </>
    ) : (
      "Update & Next"
    )}
  </button>
</div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default EditProduct;
